/* eslint-disable id-length */
import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { styled } from 'styled-components';
import { formatHex } from 'culori/fn';
import type { Hsv } from 'culori/fn';
import { THREE_SIXTY } from './consts';

const Canvas = styled.canvas`
  display: block;
  height: 100%;
  width: 100%;
`;

type HSVHueCanvasProps = {
  hsv: Hsv;
};

/**
 * Canvas component that draws a horizontal hue gradient.
 *
 * Creates a smooth gradient from red (0°) through the full hue spectrum
 * back to red (360°), maintaining consistent saturation and value.
 */
export const HSVHueCanvas = ({ hsv }: HSVHueCanvasProps): JSX.Element => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const { width, height } = canvas;
    const gradient = ctx.createLinearGradient(0, 0, width, 0);

    // Create gradient stops for full hue range
    for (let i = 0; i <= THREE_SIXTY; i++) {
      const hue = i;
      const hexColor = formatHex({ h: hue, s: hsv.s, v: hsv.v, mode: 'hsv' as const });

      if (hexColor) {
        gradient.addColorStop(i / THREE_SIXTY, hexColor);
      }
    }

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }, [hsv.s, hsv.v]); // Redraw when saturation or value changes

  return (
    <Canvas
      ref={canvasRef}
      width={THREE_SIXTY}
    />
  );
};
